import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { createApiClient } from '@/api/client';
import type { ApiClient } from '@/api/client.types';
import { createLegacyKuduToken } from '@/api/legacyKuduAuth';
import { useAuth } from '@/auth/useAuth';

import type {
  ChatBlock,
  ChatConversationTarget,
  ChatMessage,
  ChatService,
  SendMessageParams,
} from './chatTypes';

// The backend stores a conversation as typed message blocks (MessageBlock),
// shared with Kai Coach so future block types need no storage change. Today only
// markdown blocks are produced; a later change adds HTML.
interface RawMessageBlock {
  html?: string | null;
  markdown?: string | null;
  type?: number | string | null;
}

interface RawChapterChatMessage {
  blocks?: RawMessageBlock[] | null;
  created_at_utc_milli?: string | number | null;
  createdAtUtcMilli?: string | number | null;
  role?: number | string | null;
  uid?: string | null;
}

interface RawChapterChatConversation {
  messages?: RawChapterChatMessage[] | null;
  uid?: string | null;
}

interface RawGetConversationResponse {
  conversation?: RawChapterChatConversation | null;
}

interface RawSendMessageResponse {
  assistant_message?: RawChapterChatMessage | null;
  assistantMessage?: RawChapterChatMessage | null;
}

// Proto enum values arrive as either the number or the symbolic name depending on
// the serialization path, so both spellings are accepted.
const ASSISTANT_ROLE_VALUES = new Set<number | string>([1, '1', 'ASSISTANT']);
// MessageBlock.Type.HTML = 3. Anything else is treated as markdown, so blocks
// stored before HTML existed keep their meaning.
const HTML_BLOCK_VALUES = new Set<number | string>([3, '3', 'HTML']);

function normalizeString(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }
  const normalized = value.trim();
  return normalized.length > 0 ? normalized : null;
}

function normalizeTimestamp(value: unknown, fallback: number): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const normalized = normalizeString(value);
  if (!normalized) {
    return fallback;
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function isHtmlBlock(block: RawMessageBlock): boolean {
  const type = block.type;
  return type !== null && type !== undefined && HTML_BLOCK_VALUES.has(type as number | string);
}

// Block type decides the renderer. An HTML block without html, or a markdown
// block without markdown, contributes nothing rather than rendering the wrong
// kind of content.
function normalizeBlocks(message: RawChapterChatMessage): ChatBlock[] {
  return (message.blocks ?? [])
    .map((block): ChatBlock | null => {
      if (!block) {
        return null;
      }

      if (isHtmlBlock(block)) {
        const html = normalizeString(block.html);
        return html ? { html, kind: 'html' } : null;
      }

      const markdown = normalizeString(block.markdown);
      return markdown ? { kind: 'markdown', markdown } : null;
    })
    .filter((block): block is ChatBlock => block !== null);
}

// Plain-text projection of the blocks, used for user turns, retry, and as the
// accessible text of a message.
function getBlockText(blocks: ChatBlock[]): string {
  return blocks
    .map((block) => (block.kind === 'html' ? block.html : block.markdown))
    .join('\n\n');
}

function getMessageRole(message: RawChapterChatMessage): ChatMessage['role'] {
  const role = message.role;

  if (role === null || role === undefined) {
    return 'user';
  }

  return ASSISTANT_ROLE_VALUES.has(role as number | string) ? 'assistant' : 'user';
}

export function normalizeChatMessage(
  message: RawChapterChatMessage,
  index: number,
): ChatMessage | null {
  const blocks = normalizeBlocks(message);

  if (blocks.length === 0) {
    return null;
  }

  const role = getMessageRole(message);

  return {
    blocks,
    content: getBlockText(blocks),
    id: normalizeString(message.uid) ?? `${role}-${index}`,
    role,
    status: 'sent',
    timestamp: normalizeTimestamp(
      message.created_at_utc_milli ?? message.createdAtUtcMilli,
      index,
    ),
  };
}

export function normalizeConversation(
  messages: RawChapterChatMessage[] | null | undefined,
): ChatMessage[] {
  return (messages ?? [])
    .map((message, index) => normalizeChatMessage(message, index))
    .filter((message): message is ChatMessage => message !== null);
}

export class KuduChatService implements ChatService {
  constructor(private readonly apiClient: ApiClient) {}

  async getConversation(target: ChatConversationTarget): Promise<ChatMessage[]> {
    const response = await this.apiClient.invokeKuduMethod<
      RawGetConversationResponse,
      { component_uid: string; course_uid: string; user_id: string }
    >('GetChapterChatConversation', {
      component_uid: target.componentId,
      course_uid: target.courseId,
      user_id: target.userId,
    });

    return normalizeConversation(response?.conversation?.messages);
  }

  async sendMessage(params: SendMessageParams): Promise<ChatMessage> {
    const response = await this.apiClient.invokeKuduMethod<
      RawSendMessageResponse,
      {
        component_uid: string;
        course_uid: string;
        message: string;
        user_id: string;
      }
    >('SendChapterChatMessage', {
      component_uid: params.componentId,
      course_uid: params.courseId,
      message: params.message,
      user_id: params.userId,
    });

    const raw = response?.assistant_message ?? response?.assistantMessage ?? null;
    const message = raw ? normalizeChatMessage(raw, 0) : null;

    if (!message) {
      throw new Error('AI response was incomplete. Please try again.');
    }

    return message;
  }

  async resetConversation(target: ChatConversationTarget): Promise<void> {
    await this.apiClient.invokeKuduMethod<
      { deleted?: boolean | null },
      { component_uid: string; course_uid: string; user_id: string }
    >('ResetChapterChatConversation', {
      component_uid: target.componentId,
      course_uid: target.courseId,
      user_id: target.userId,
    });
  }
}

export function useKuduChatService(): ChatService | null {
  const { getIdToken, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  return useMemo(() => {
    if (!user) {
      return null;
    }

    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    const client = createApiClient({
      baseUrl: import.meta.env.VITE_API_BASE_URL || '',
      getIdToken,
      getLegacyToken: async () => createLegacyKuduToken(user),
      onAuthError: () =>
        navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`, {
          replace: true,
        }),
    });

    return new KuduChatService(client);
  }, [getIdToken, location.hash, location.pathname, location.search, navigate, user]);
}
